import type { FC } from 'react';
import type { PassiveScanResults } from './types';
import styles from './PassiveScans.module.css';

interface ScanResultsProps {
  results: PassiveScanResults;
}

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'info'] as const;

export const ScanResults: FC<ScanResultsProps> = ({ results }) => {
  const { findings, summary } = results;

  return (
    <div className={styles.section}>
      <div className={styles.resultsHeader}>
        <h2 className={styles.sectionTitle}>
          Scan Results
          <span className={styles.badge}>
            {summary.total} finding{summary.total !== 1 ? 's' : ''}
          </span>
        </h2>
        <div className={styles.severitySummary}>
          {SEVERITY_ORDER.map((severity) => {
            const count = summary.bySeverity[severity];
            if (!count) return null;
            return (
              <span key={severity} className={styles.severityPill} data-severity={severity}>
                {count} {severity}
              </span>
            );
          })}
        </div>
      </div>

      {findings.length === 0 ? (
        <p className={styles.emptyStrategies}>No issues found. Your API passed all passive checks.</p>
      ) : (
        <div className={styles.findingsList}>
          {findings.map((finding, index) => (
            <div
              key={`${finding.severity}-${finding.strategy}-${finding.title}-${index}`}
              className={styles.findingCard}
            >
              <div className={styles.findingHeader}>
                <span className={styles.severityPill} data-severity={finding.severity}>
                  {finding.severity}
                </span>
                <span className={styles.findingStrategy}>{finding.strategy}</span>
                {finding.endpoint && <code className={styles.findingEndpoint}>{finding.endpoint}</code>}
              </div>
              <h3 className={styles.findingTitle}>{finding.title}</h3>
              <p className={styles.findingDescription}>{finding.description}</p>
              {finding.details && <pre className={styles.findingDetails}>{finding.details}</pre>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
